// Optimistic cache edits for the row-level priority toggle.
//
// THE RULE, corrected. An earlier version of this file said the chronological
// list "shows flagged and unflagged RFQs alike, so a toggle updates the row in
// place", and treated the priority board as the one special case that removes
// rows. That premise was wrong: the list has its own "🚩 Flagged" checkbox
// (rfq-list.tsx), so it can be flag-filtered too. Unflagging there left an
// unflagged RFQ sitting in a flagged-only list until the refetch blinked it
// away — the exact flicker the board case was written to avoid.
//
// There is only ever ONE rule, and it is a property of the QUERY, not of the
// component doing the rendering:
//
//   unflagging a row that a query selected BECAUSE it was flagged removes it;
//   anything else updates in place.
//
// The board is not special — it is just `flagged=true&board=true`, the most
// obvious member of the flag-filtered family.
//
// These functions take a QueryClient rather than living inside the mutation so
// they can be tested against a real cache with no React and no DOM, which
// matters because vitest runs this repo in `environment: 'node'`.

import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import type { RfqListItem, RfqListResponse } from '@/lib/api';

/** Every RFQ list query is keyed `['rfqs', …]`; this is their common prefix. */
export const RFQ_QUERY_PREFIX = ['rfqs'] as const;

/** The priority board's exact key. Exported so rfq-board.tsx cannot drift. */
export const BOARD_QUERY_KEY = ['rfqs', 'board'] as const;

/**
 * Did this query select its rows BECAUSE they are flagged?
 *
 * Parsed with URLSearchParams rather than a substring test: a free-text search
 * for the word "flagged" encodes as `q=flagged%3Dtrue`, which a naive
 * `.includes('flagged=true')` would misread as a filter and start deleting rows
 * out of a search result.
 */
export function isFlagFilteredKey(queryKey: QueryKey): boolean {
  const segment = queryKey[1];
  if (segment === 'board') return true;
  if (typeof segment !== 'string') return false;
  return new URLSearchParams(segment).get('flagged') === 'true';
}

/** Flip `isFlagged` on one row. Returns the input when the id is absent. */
export function setFlagInRows(
  rfqs: readonly RfqListItem[],
  id: string,
  isFlagged: boolean,
): readonly RfqListItem[] {
  let found = false;
  const next = rfqs.map((r) => {
    if (r.id !== id) return r;
    found = true;
    // Unflagging clears the note, mirroring the server: PATCH sets
    // flag_note = null whenever isFlagged goes false (server/routes/rfqs.ts).
    return { ...r, isFlagged, ...(isFlagged ? {} : { flagNote: null }) };
  });
  return found ? next : rfqs;
}

/** Remove one row. Returns the input when the id is absent. */
export function dropRow(
  rfqs: readonly RfqListItem[],
  id: string,
): readonly RfqListItem[] {
  const next = rfqs.filter((r) => r.id !== id);
  return next.length === rfqs.length ? rfqs : next;
}

/** The one rule, applied to one page of rows. */
export function applyToRows(
  rfqs: readonly RfqListItem[],
  id: string,
  isFlagged: boolean,
  flagFiltered: boolean,
): readonly RfqListItem[] {
  return !isFlagged && flagFiltered
    ? dropRow(rfqs, id)
    : setFlagInRows(rfqs, id, isFlagged);
}

type ListCache = InfiniteData<RfqListResponse> | RfqListResponse | undefined;

/**
 * Apply the rule to one cache entry, whichever shape it has.
 *
 * Two shapes are genuinely in play: the chronological list is an infinite query
 * (`{ pages, pageParams }`) and the board is a single response (`{ rfqs }`).
 * Detecting the shape rather than assuming one is what stops a silent no-op.
 */
export function applyToCacheEntry(
  data: ListCache,
  id: string,
  isFlagged: boolean,
  flagFiltered: boolean,
): ListCache {
  if (!data) return data;

  if ('pages' in data) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        rfqs: applyToRows(page.rfqs, id, isFlagged, flagFiltered) as RfqListResponse['rfqs'],
      })),
    };
  }

  if ('rfqs' in data) {
    return {
      ...data,
      rfqs: applyToRows(data.rfqs, id, isFlagged, flagFiltered) as RfqListResponse['rfqs'],
    };
  }

  return data;
}

/** What `restoreRfqCaches` needs to undo an optimistic write. */
export type CacheSnapshot = ReadonlyArray<[QueryKey, unknown]>;

/**
 * Optimistically apply a flag toggle across every cached RFQ list.
 *
 * Returns a snapshot taken BEFORE the write, for rollback. Callers are expected
 * to have cancelled in-flight queries first — a refetch that started before the
 * click would otherwise land afterwards and undo this.
 */
export function applyOptimisticFlag(
  queryClient: QueryClient,
  id: string,
  isFlagged: boolean,
): CacheSnapshot {
  const snapshot = queryClient.getQueriesData({ queryKey: RFQ_QUERY_PREFIX });

  for (const [key] of snapshot) {
    const flagFiltered = isFlagFilteredKey(key);
    queryClient.setQueryData<ListCache>(key, (old) =>
      applyToCacheEntry(old, id, isFlagged, flagFiltered),
    );
  }

  return snapshot;
}

/**
 * Put every cache back exactly as it was.
 *
 * Restoring rather than invalidating on failure is deliberate: a toggle that
 * did not happen should leave the operator looking at what they had, not at a
 * spinner that implies something is still in progress.
 */
export function restoreRfqCaches(queryClient: QueryClient, snapshot: CacheSnapshot): void {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}
